"use client";

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFormContext } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { CalendarIcon, Users } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn, formatDate } from "@/lib/utils";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ImageUpload } from "@/components/ui/image-upload";
import { nigerianBreeds } from "@/utils/items.ts";

type GreatGrandparentalInfoFormProps = {
  setPaternalGrandSireGrandfatherImageFile: (file: File) => void;
  setPaternalGrandSireGrandmotherImageFile: (file: File) => void;
  setPaternalGrandDamGrandfatherImageFile: (file: File) => void;
  setPaternalGrandDamGrandmotherImageFile: (file: File) => void;
  setMaternalGrandSireGrandfatherImageFile: (file: File) => void;
  setMaternalGrandSireGrandmotherImageFile: (file: File) => void;
  setMaternalGrandDamGrandfatherImageFile: (file: File) => void;
  setMaternalGrandDamGrandmotherImageFile: (file: File) => void;
};

export function GreatGrandparentalInfoForm({
  setPaternalGrandSireGrandfatherImageFile,
  setPaternalGrandSireGrandmotherImageFile,
  setPaternalGrandDamGrandfatherImageFile,
  setPaternalGrandDamGrandmotherImageFile,
  setMaternalGrandSireGrandfatherImageFile,
  setMaternalGrandSireGrandmotherImageFile,
  setMaternalGrandDamGrandfatherImageFile,
  setMaternalGrandDamGrandmotherImageFile,
}: GreatGrandparentalInfoFormProps) {
  const { control, setValue, watch } = useFormContext();

  // State for all great-grandparent birth dates
  const [dates, setDates] = useState({
    paternalGrandSireGrandDam: watch("paternalGrandSireGrandDamBirthDate")
      ? new Date(watch("paternalGrandSireGrandDamBirthDate"))
      : undefined,
    paternalGrandSireGrandSire: watch("paternalGrandSireGrandSireBirthDate")
      ? new Date(watch("paternalGrandSireGrandSireBirthDate"))
      : undefined,
    paternalGrandDamGrandDam: watch("paternalGrandDamGrandDamBirthDate")
      ? new Date(watch("paternalGrandDamGrandDamBirthDate"))
      : undefined,
    paternalGrandDamGrandSire: watch("paternalGrandDamGrandSireBirthDate")
      ? new Date(watch("paternalGrandDamGrandSireBirthDate"))
      : undefined,
    maternalGrandSireGrandDam: watch("maternalGrandSireGrandDamBirthDate")
      ? new Date(watch("maternalGrandSireGrandDamBirthDate"))
      : undefined,
    maternalGrandSireGrandSire: watch("maternalGrandSireGrandSireBirthDate")
      ? new Date(watch("maternalGrandSireGrandSireBirthDate"))
      : undefined,
    maternalGrandDamGrandDam: watch("maternalGrandDamGrandDamBirthDate")
      ? new Date(watch("maternalGrandDamGrandDamBirthDate"))
      : undefined,
    maternalGrandDamGrandSire: watch("maternalGrandDamGrandSireBirthDate")
      ? new Date(watch("maternalGrandDamGrandSireBirthDate"))
      : undefined,
  });

  const handleDateSelect = (
    key: string,
    fieldName: string,
    date: Date | undefined,
  ) => {
    setDates((prev) => ({ ...prev, [key]: date }));
    if (date) {
      setValue(fieldName, formatDate(date));
    }
  };

  // Updated renderAnimalSection signature
  const renderAnimalSection = (
    dateKey: keyof typeof dates,
    title: string,
    nameField: string,
    dgrField: string,
    breedField: string,
    birthDateField: string,
    imageField: string,
    setImageFile: (file: File) => void,
  ) => (
    <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
      <h4 className="text-md font-semibold mb-4">{title}</h4>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-4">
          <FormField
            control={control}
            name={nameField}
            render={({ field }) => (
              <FormItem>
                <FormLabel className={"text-[#008c8fff]"}>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name={dgrField}
            render={({ field }) => (
              <FormItem>
                <FormLabel className={"text-[#008c8fff]"}>DGR</FormLabel>
                <FormControl>
                  <Input placeholder="Enter DGR" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name={birthDateField}
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel className={"text-[#008c8fff]"}>
                  Date of birth
                </FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !dates[dateKey] && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dates[dateKey] ? (
                          format(dates[dateKey]!, "PPP")
                        ) : (
                          <span>Select date</span>
                        )}
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value ? new Date(field.value) : undefined}
                      onSelect={(date) =>
                        handleDateSelect(dateKey, birthDateField, date)
                      }
                      disabled={(date: any) =>
                        date > new Date() || date < new Date("1900-01-01")
                      }
                      captionLayout="dropdown"
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* <FormField */}
          {/*   control={control} */}
          {/*   name={birthDateField} */}
          {/*   render={({}) => ( */}
          {/*     <FormItem className="flex flex-col"> */}
          {/*       <FormLabel className={"text-[#008c8fff]"}>Birth Date</FormLabel> */}
          {/*       <Popover> */}
          {/*         <PopoverTrigger asChild> */}
          {/*           <FormControl> */}
          {/*             <Button */}
          {/*               variant="outline" */}
          {/*               className={cn( */}
          {/*                 "w-full pl-3 text-left font-normal", */}
          {/*                 !dates[dateKey as keyof typeof dates] && */}
          {/*                   "text-muted-foreground", */}
          {/*               )} */}
          {/*             > */}
          {/*               <CalendarIcon className="mr-2 h-4 w-4" /> */}
          {/*               {dates[dateKey as keyof typeof dates] ? ( */}
          {/*                 format(dates[dateKey as keyof typeof dates]!, "PPP") */}
          {/*               ) : ( */}
          {/*                 <span>Select date</span> */}
          {/*               )} */}
          {/*             </Button> */}
          {/*           </FormControl> */}
          {/*         </PopoverTrigger> */}
          {/*         <PopoverContent */}
          {/*           className="w-auto p-0" */}
          {/*           align="start" */}
          {/*           side="bottom" */}
          {/*         > */}
          {/*           <Calendar */}
          {/*             mode="single" */}
          {/*             selected={dates[dateKey as keyof typeof dates]} */}
          {/*             onSelect={(date) => */}
          {/*               handleDateSelect(dateKey, birthDateField, date) */}
          {/*             } */}
          {/*             initialFocus */}
          {/*             disabled={(date) => date > new Date()} */}
          {/*           /> */}
          {/*         </PopoverContent> */}
          {/*       </Popover> */}
          {/*       <FormMessage /> */}
          {/*     </FormItem> */}
          {/*   )} */}
          {/* /> */}

          <FormField
            control={control}
            name={breedField}
            render={({ field }) => (
              <FormItem>
                <FormLabel className={"text-[#008c8fff]"}>Breed</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select breed" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent
                    position="popper"
                    className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white "
                  >
                    {nigerianBreeds.map((option) => (
                      <SelectItem
                        className={"overflow-y-auto z-50 background-white"}
                        key={option.breed}
                        value={option.breed}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div>
          <FormField
            control={control}
            name={imageField}
            render={({ field }) => (
              <FormItem>
                <FormLabel className={"text-[#008c8fff]"}>Image</FormLabel>
                <FormControl>
                  <ImageUpload
                    value={field.value}
                    onChange={(file) => {
                      field.onChange(file);
                      if (file instanceof File) {
                        setImageFile(file);
                      }
                    }}
                    label={`Upload ${title} Image`}
                    description={`Add a photo of the ${title.toLowerCase()}`}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center gap-2">
          <Users className="h-6 w-6 text-purple-600" />
          <h2 className="text-2xl font-bold">
            Great-Grandparental Information
          </h2>
        </div>
        <p className="text-muted-foreground">
          Fourth generation ancestry records with visual documentation
        </p>
      </div>

      <Tabs defaultValue="paternal-grandsire" className="w-full">
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4">
          <TabsTrigger value="paternal-grandsire" className="text-xs">
            Pat. Grandsire
          </TabsTrigger>
          <TabsTrigger value="paternal-grandam" className="text-xs">
            Pat. Grandam
          </TabsTrigger>
          <TabsTrigger value="maternal-grandsire" className="text-xs">
            Mat. Grandsire
          </TabsTrigger>
          <TabsTrigger value="maternal-grandam" className="text-xs">
            Mat. Grandam
          </TabsTrigger>
        </TabsList>

        <TabsContent value="paternal-grandsire" className="space-y-6 mt-6">
          <div className="bg-slate-100 dark:bg-slate-800 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-6">
              Paternal Grandsire's Parents
            </h3>
            <div className="space-y-6">
              {renderAnimalSection(
                "paternalGrandSireGrandDam",
                "Great-Grandam (Paternal Grandsire's Mother)",
                "paternalGrandSireGrandDamName",
                "paternalGrandSireGrandDamDGR",
                "paternalGrandSireGrandDamBreed",
                "paternalGrandSireGrandDamBirthDate",
                "paternalGrandSireGrandDamImage",
                setPaternalGrandSireGrandmotherImageFile,
              )}

              {renderAnimalSection(
                "paternalGrandSireGrandSire",
                "Great-Grandsire (Paternal Grandsire's Father)",
                "paternalGrandSireGrandSireName",
                "paternalGrandSireGrandSireDGR",
                "paternalGrandSireGrandSireBreed",
                "paternalGrandSireGrandSireBirthDate",
                "paternalGrandSireGrandSireImage",
                setPaternalGrandSireGrandfatherImageFile,
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="paternal-grandam" className="space-y-6 mt-6">
          <div className="bg-slate-100 dark:bg-slate-800 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-6">
              Paternal Grandam's Parents
            </h3>
            <div className="space-y-6">
              {renderAnimalSection(
                "paternalGrandDamGrandDam",
                "Great-Grandam (Paternal Grandam's Mother)",
                "paternalGrandDamGrandDamName",
                "paternalGrandDamGrandDamDGR",
                "paternalGrandDamGrandDamBreed",
                "paternalGrandDamGrandDamBirthDate",
                "paternalGrandDamGrandDamImage",
                setPaternalGrandDamGrandmotherImageFile,
              )}

              {renderAnimalSection(
                "paternalGrandDamGrandSire",
                "Great-Grandsire (Paternal Grandam's Father)",
                "paternalGrandDamGrandSireName",
                "paternalGrandDamGrandSireDGR",
                "paternalGrandDamGrandSireBreed",
                "paternalGrandDamGrandSireBirthDate",
                "paternalGrandDamGrandSireImage",
                setPaternalGrandDamGrandfatherImageFile,
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="maternal-grandsire" className="space-y-6 mt-6">
          <div className="bg-slate-100 dark:bg-slate-800 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-6">
              Maternal Grandsire's Parents
            </h3>
            <div className="space-y-6">
              {renderAnimalSection(
                "maternalGrandSireGrandDam",
                "Great-Grandam (Maternal Grandsire's Mother)",
                "maternalGrandSireGrandDamName",
                "maternalGrandSireGrandDamDGR",
                "maternalGrandSireGrandDamBreed",
                "maternalGrandSireGrandDamBirthDate",
                "maternalGrandSireGrandDamImage",
                setMaternalGrandSireGrandmotherImageFile,
              )}

              {renderAnimalSection(
                "maternalGrandSireGrandSire",
                "Great-Grandsire (Maternal Grandsire's Father)",
                "maternalGrandSireGrandSireName",
                "maternalGrandSireGrandSireDGR",
                "maternalGrandSireGrandSireBreed",
                "maternalGrandSireGrandSireBirthDate",
                "maternalGrandSireGrandSireImage",
                setMaternalGrandSireGrandfatherImageFile,
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="maternal-grandam" className="space-y-6 mt-6">
          <div className="bg-slate-100 dark:bg-slate-800 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-6">
              Maternal Grandam's Parents
            </h3>
            <div className="space-y-6">
              {renderAnimalSection(
                "maternalGrandDamGrandDam",
                "Great-Grandam (Maternal Grandam's Mother)",
                "maternalGrandDamGrandDamName",
                "maternalGrandDamGrandDamDGR",
                "maternalGrandDamGrandDamBreed",
                "maternalGrandDamGrandDamBirthDate",
                "maternalGrandDamGrandDamImage",
                setMaternalGrandDamGrandmotherImageFile,
              )}

              {renderAnimalSection(
                "maternalGrandDamGrandSire",
                "Great-Grandsire (Maternal Grandam's Father)",
                "maternalGrandDamGrandSireName",
                "maternalGrandDamGrandSireDGR",
                "maternalGrandDamGrandSireBreed",
                "maternalGrandDamGrandSireBirthDate",
                "maternalGrandDamGrandSireImage",
                setMaternalGrandDamGrandfatherImageFile,
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Card className="p-4 bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800">
        <div className="flex items-start gap-3">
          <Users className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
          <div className="space-y-2">
            <h4 className="font-medium text-purple-900 dark:text-purple-100">
              Great-Grandparental Records
            </h4>
            <p className="text-sm text-purple-700 dark:text-purple-200">
              Great-grandparental information provides comprehensive
              four-generation pedigree data essential for advanced genetic
              analysis and breeding decisions. This detailed ancestry helps
              identify genetic patterns, inbreeding coefficients, and lineage
              diversity. Visual documentation aids in phenotype tracking and
              historical record maintenance.
            </p>
            <div className="text-xs text-purple-600 dark:text-purple-300">
              <p>
                <strong>Tip:</strong> Complete records improve genetic
                evaluation accuracy and breeding program effectiveness.
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
